import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import Header from '../../components/Header';
import Loader from '../../components/Loader';
import AmountInput from './components/AmountInput';
import BreakdownCard from './components/BreakdownCard';
import ModeRow from './components/ModeRow';
import ConfirmSheet from './components/ConfirmSheet';

import useInitiateWithdrawal from '../../api/useInitiateWithdrawal';
import { useUserStore } from '../../storage/userState';
import useWithdrawBreakdown from '../../api/useWithdrawBreakdown';
import useProcessWithdrawal from '../../api/useProcessWithdrawal';

import {
  useWithdrawStore,
  setWithdrawAmount,
  setSelectedWithdrawMode,
  setModeJustAdded,
  setShownNudgeId,
} from '../../storage/withdrawState';
import { NAVIGATION_ROUTES, CLEVERTAP_EVENTS } from '../../constant';
import { pushClevertapEvent } from '../../utils/analytics/clevertap';
import {
  getAmountErrorKey,
  getAmountLimits,
  getAvailableModes,
  getOfferedModes,
  getDefaultMode,
  isSessionValid,
  isWithdrawAvailable,
  parseAmountInput,
  formatRupees,
} from './helper';
import styles from './styles.module.css';

/**
 * Withdrawal screen — the PAYOUT flow.
 *
 * Mirrors Android's `WithdrawalFragment`: the screen initialises a session,
 * recalculates a server-built breakdown as the user types, and submits through
 * a server-built confirm sheet. Nothing here computes money.
 */
const Withdraw: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    amount,
    selectedMode,
    modeJustAdded,
    sessionTimestamp,
    shownNudgeId,
  } = useWithdrawStore();

  const { userDetails } = useUserStore();

  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [hasPrefilled, setHasPrefilled] = useState(false);
  const hasReinitialisedRef = useRef(false);

  const {
    screenData,
    isLoading: isScreenLoading,
    error: screenError,
    refetch: refetchScreen,
  } = useInitiateWithdrawal({ userInputAmount: amount });

  const sessionId = screenData?.screenData?.sessionId;
  const payoutData = screenData?.withdrawData;
  const limits = useMemo(() => getAmountLimits(screenData), [screenData]);
  const availableModes = useMemo(
    () => getAvailableModes(screenData, userDetails),
    [screenData, userDetails],
  );
  const offeredModes = useMemo(() => getOfferedModes(screenData), [screenData]);

  const {
    withdrawBreakdown,
    isStale,
    error: breakdownError,
  } = useWithdrawBreakdown({
    amount,
    sessionId,
    isWithdrawAvailable: isWithdrawAvailable(screenData),
  });

  const { submitWithdrawal, isSubmitting } = useProcessWithdrawal();

  const amountErrorKey = getAmountErrorKey(amount, limits);

  /* ---- session freshness -------------------------------------------- */
  // A lapsed session would be rejected at submit, so re-open it first.
  // Bounded to one forced re-init per mount: if the server keeps handing back
  // an already-stale `timeStamp`, retrying on every render would spin forever.
  useEffect(() => {
    if (!screenData) return;
    if (isSessionValid(screenData, sessionTimestamp)) return;
    if (hasReinitialisedRef.current) return;

    hasReinitialisedRef.current = true;
    refetchScreen();
  }, [screenData, sessionTimestamp, refetchScreen]);

  // Re-open the session the moment it lapses while the screen is still open.
  useEffect(() => {
    const ttl = screenData?.screenData?.cacheTTL;
    const issuedAt = screenData?.screenData?.timeStamp ?? sessionTimestamp;
    if (!ttl || !issuedAt) return;

    const msLeft = issuedAt + ttl - Date.now();
    if (msLeft <= 0) return;

    const timer = setTimeout(() => {
      hasReinitialisedRef.current = false;
      refetchScreen();
    }, msLeft);
    return () => clearTimeout(timer);
  }, [screenData, sessionTimestamp, refetchScreen]);

  /* ---- prefill + default mode ---------------------------------------- */
  useEffect(() => {
    if (!screenData || hasPrefilled) return;

    const prefill = screenData.experimentData?.prefillAmount;
    if (prefill && !amount) setWithdrawAmount(prefill);
    setHasPrefilled(true);
  }, [screenData, hasPrefilled, amount]);

  useEffect(() => {
    if (!screenData) return;
    // Keep the selection valid when the server's available modes change.
    if (selectedMode && availableModes.includes(selectedMode)) return;

    const nextMode = getDefaultMode(screenData, userDetails);
    if (nextMode !== selectedMode) setSelectedWithdrawMode(nextMode);
  }, [screenData, selectedMode, availableModes, userDetails]);

  /* ---- returning from add-mode ---------------------------------------- */
  useEffect(() => {
    if (!modeJustAdded) return;
    // A mode was just linked — pull a fresh session so the server's mode data
    // and limits reflect it, then clear the handoff.
    setModeJustAdded(false);
    refetchScreen();
  }, [modeJustAdded, refetchScreen]);

  /* ---- analytics ------------------------------------------------------- */
  useEffect(() => {
    pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_WITHDRAW_SCREEN);
  }, []);

  /* ---- errors ---------------------------------------------------------- */
  useEffect(() => {
    if (breakdownError) {
      toast.error((breakdownError as Error)?.message || t('errorOccurred'));
    }
  }, [breakdownError, t]);

  /* ---- handlers -------------------------------------------------------- */
  const handleAmountChange = (raw: string) => {
    setWithdrawAmount(parseAmountInput(raw));
  };

  const handleManageModes = () => {
    navigate(NAVIGATION_ROUTES.WITHDRAW_MODES);
  };

  const handleAddMode = () => {
    // Only offer what the server will accept — linking a mode outside
    // `withdrawalOptions` produces one it still will not withdraw to.
    navigate(NAVIGATION_ROUTES.WITHDRAW_ADD_MODE, {
      state: { offeredModes },
    });
  };

  const handleOpenConfirm = () => {
    pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_WITHDRAW_CTA_CLICKED, {
      amount,
      mode: selectedMode,
    });

    // No usable mode saved: the server's own copy ("add account to receive
    // money in the next step") says to proceed and collect it now, so send the
    // user to add one rather than dead-ending on a disabled button.
    if (!availableModes.length) {
      handleAddMode();
      return;
    }

    pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_WITHDRAW_CONFIRM_BS_VIEW);
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    if (isSubmitting) return;

    pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_WITHDRAW_CONFIRM_CLICKED, {
      amount,
      mode: selectedMode,
    });

    submitWithdrawal(
      {
        type: 'PAYOUT',
        amountInRupees: Number(amount),
        sessionId,
        transferMode: selectedMode ?? null,
        // Echoes back the nudge the wallet gate showed, as Android does.
        nudgeIdShown: shownNudgeId ?? null,
        localeSelected:
          screenData?.experimentData?.localeData?.selectedLocale ?? null,
      },
      {
        onSuccess: (result) => {
          setConfirmOpen(false);
          // Spent — a later withdrawal must not re-report this nudge.
          setShownNudgeId(undefined);
          navigate(NAVIGATION_ROUTES.WITHDRAW_STATUS, {
            state: { statusScreen: result?.statusScreen },
          });
        },
        onError: (error) => {
          setConfirmOpen(false);
          toast.error((error as Error)?.message || t('errorOccurred'));
        },
      },
    );
  };

  /* ---- render ---------------------------------------------------------- */
  // Also covers the idle window before silent login populates user details,
  // during which the session query is deliberately held back.
  if (isScreenLoading || (!screenData && !screenError)) return <Loader />;

  if (screenError && !screenData) {
    return (
      <div className={styles.withdrawPage}>
        <Header
          pageTitle={t('withdrawTitle')}
          routeValue={NAVIGATION_ROUTES.WALLET}
        />
        <div className={styles.errorState}>
          <p className="fontSizeSm14">
            {(screenError as Error)?.message || t('errorOccurred')}
          </p>
          <button
            type="button"
            className="btn primaryBtn mdHeight"
            onClick={() => refetchScreen()}
          >
            {t('retryLabel')}
          </button>
        </div>
      </div>
    );
  }

  const hasMode = availableModes.length > 0;
  const isAmountValid =
    Boolean(amount) && amount! > 0 && amountErrorKey === null;
  // A missing mode no longer blocks the CTA — it routes to add-mode instead.
  const canSubmit = isAmountValid && !isStale && !isSubmitting;

  // With nothing saved the server names the CTA per offered mode
  // ("Continue with Bank Account"); otherwise `totalData` is the button label.
  const ctaTextMap = payoutData?.modeData?.modeSelectionData?.ctaTextMap;
  const addModeCtaText =
    !hasMode && offeredModes.length === 1
      ? ctaTextMap?.[offeredModes[0]]
      : undefined;
  const ctaText =
    addModeCtaText ||
    withdrawBreakdown?.totalData?.text ||
    withdrawBreakdown?.confirmData?.ctaData?.text ||
    t('withdrawLabel');

  return (
    <div className={styles.withdrawPage}>
      <Header
        pageTitle={screenData?.screenData?.screenTitle || t('withdrawTitle')}
        routeValue={NAVIGATION_ROUTES.WALLET}
      />

      <div className={styles.balanceStrip}>
        <span className="fontSizeSm12 fontColor666">
          {screenData?.screenData?.balanceLabel || t('winningsBalanceLabel')}
        </span>
        <span className="fontSizeSm fontWeightBold">
          {formatRupees(screenData?.screenData?.balance)}
        </span>
      </div>

      <div className={styles.withdrawBody}>
        <AmountInput
          value={amount}
          onChange={handleAmountChange}
          limits={limits}
          errorKey={amountErrorKey}
          autoFocus={screenData?.experimentData?.openKeyboard}
          placeholder={payoutData?.placeHolder?.text}
        />

        <BreakdownCard data={withdrawBreakdown} isLoading={isStale} />

        <ModeRow
          payoutData={payoutData}
          userDetails={userDetails}
          availableModes={availableModes}
          selectedMode={selectedMode}
          onSelectMode={setSelectedWithdrawMode}
          onAddMode={handleAddMode}
          onManageModes={handleManageModes}
        />
      </div>

      <div className={styles.ctaBar}>
        <button
          type="button"
          className={clsx('btn primaryBtn')}
          disabled={!canSubmit}
          onClick={handleOpenConfirm}
        >
          {ctaText}
        </button>
      </div>

      <ConfirmSheet
        isOpen={isConfirmOpen}
        data={withdrawBreakdown?.confirmData}
        isSubmitting={isSubmitting}
        onConfirm={handleConfirm}
        onClose={() => !isSubmitting && setConfirmOpen(false)}
      />
    </div>
  );
};

export default Withdraw;
